import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { LogtoProvider } from '@logto/react';
import { Provider as ReduxProvider } from 'react-redux';
import { RouterProvider } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import { retrieveLaunchParams } from '@tma.js/sdk-react';

import { store } from '@/store';
import { router } from '@/routes/router';
import { i18n } from '@/lib/i18n';
import { initTelegram } from '@/lib/telegram';
import { applyPalette, loadStoredAppearance } from '@/lib/appearance';
import { coloreChromeIniziale, paletteIniziale } from '@/lib/v3';
import { logtoConfig, refreshBetaRoleAtBoot } from '@/lib/logto';
import { rottaSpiderProvaInBuild } from '@/lib/solitario/rottaSpiderProva';
import { stashPendingStartParam } from '@/lib/startParam';
import { describeDmConsent } from '@/lib/dmConsent';
import { dbg } from '@/lib/logto';
import { EnvUnsupported } from '@/components/common/EnvUnsupported';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { AuthCallback } from '@/routes/AuthCallback';
import { env } from '@/env';
import { registerChunkPreloadErrorHandler } from '@/lib/chunkReload';

import './styles/fonts.css';
import './styles/globals.css';
import { setupMockEnv } from './mockEnv';

// Il segnale piu' precoce su un chunk di un rilascio vecchio che non c'e' piu'
// all'origine (stg-tma serve solo il rilascio corrente). Registrato subito, prima di
// ogni render: una schermata lazy puo' essere la prima cosa che l'utente apre.
registerChunkPreloadErrorHandler();

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found');
const root = ReactDOM.createRoot(rootEl);

// The auth-bridge round trip lands here from the system browser after Logto
// auth (PROD flow): tma.qlash.gg/auth/callback?code=...&state=... in Safari,
// with NO Telegram WebApp context. Without this short-circuit the normal
// bootstrap would call retrieveLaunchParams(), throw, render <EnvUnsupported>
// — and the BridgeCallback that deep-links the code back into the bot would
// never run. Render AuthCallback directly so BridgeCallback can do its job
// (build the t.me?startapp=auth_<code> link and navigate to it).
function isBridgeCallback(): boolean {
  if (window.location.pathname !== '/auth/callback') return false;
  const initData = (window as unknown as {
    Telegram?: { WebApp?: { initData?: string } };
  }).Telegram?.WebApp?.initData;
  return !initData;
}

async function bootstrap() {
  if (isBridgeCallback()) {
    // BridgeCallback only reads window.location.search + writes window.location.href,
    // no router/Logto/Redux providers required. Wrapped in ErrorBoundary so a
    // crash here still surfaces something instead of a white screen.
    root.render(
      <StrictMode>
        <ErrorBoundary>
          <AuthCallback />
        </ErrorBoundary>
      </StrictMode>,
    );
    return;
  }

  try {
    await setupMockEnv();

    const launchParams = retrieveLaunchParams();
    const platform = launchParams.tgWebAppPlatform;
    const debug = (launchParams.tgWebAppStartParam || '').includes('debug') || env.isDev;

    // 🔴 QUI, e non piu' tardi: su mobile il login RILANCIA la mini app con
    // `startapp=auth_<code>`, e i launch params del rilancio SOSTITUISCONO
    // questi. Un payload letto dopo il login e' il payload del bridge, mai
    // quello del link che l'ospite ha aperto. Questa riga sta prima di ogni
    // render, quindi prima che «Sign in» possa essere premuto: e' il PRIMA a
    // essere la correzione. Vedi `lib/startParam.ts`.
    // Il secondo argomento (la rotta della prova dello Spider della build, `undefined` in prod) si toglie nella PR del motore dello Spider: v. `lib/startParam.ts`.
    stashPendingStartParam(launchParams.tgWebAppStartParam, { rottaSpiderProva: rottaSpiderProvaInBuild() });

    // Il permesso di scrivere in DM (`allows_write_to_pm`) sta in QUESTI launch params
    // e non altrove — e la stessa ragione della riga sopra vale al contrario: dopo il
    // rilancio del login i params sono quelli del ponte, e l'utente c'e' lo stesso, ma
    // leggerlo qui e' l'unico punto in cui e' quello dell'apertura vera. Una riga sola,
    // al boot: e' un'osservazione per ADR-0107, non un ramo di comportamento.
    dbg(describeDmConsent(launchParams.tgWebAppData?.user));

    await initTelegram({
      debug,
      eruda: debug && env.enableEruda && ['ios', 'android'].includes(platform),
      mockForMacOS: platform === 'macos',
      coloreChrome: coloreChromeIniziale(),
    });

    // Apply the persisted theme before the first React paint (no FOUC). The
    // slice hydrates the same prefs into Redux; useAppearance() keeps it in
    // sync from here on.
    // Con la v3 prenotata (flag di build + interruttore acceso, senza ancora i ruoli) il primo dipinto e' gia' il suo:
    // `useAppearance` poi applica il predicato vero e, se manca `beta`, torna al tema salvato.
    applyPalette(await paletteIniziale(loadStoredAppearance()));

    root.render(
      <StrictMode>
        <ErrorBoundary>
          <LogtoProvider config={logtoConfig}>
            <ReduxProvider store={store}>
              <I18nextProvider i18n={i18n}>
                <RouterProvider router={router} />
              </I18nextProvider>
            </ReduxProvider>
          </LogtoProvider>
        </ErrorBoundary>
      </StrictMode>,
    );

    // I-821: SOLO staging (flag in `.env.staging`), un refresh per apertura se manca `beta`.
    // Non si aspetta: la app non e' ferma su di lui, e i gate rileggono da soli a token nuovo
    // (`qlash:tokens-changed`). Un fallimento non produce niente di visibile.
    void refreshBetaRoleAtBoot();
  } catch (err) {

    console.error('[bootstrap] failed to initialize:', err);
    root.render(<EnvUnsupported />);
  }
}

void bootstrap();
